import React, { useEffect, useState } from "react";

// Open-Meteo API: https://open-meteo.com/en/docs/historical-weather-api
// Props: lat, lon, todayCondition, cityName
const WeatherHistory = ({ lat, lon, cityName }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);

  // Get last 3 years for this day
  const getPastDates = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const years = [1, 2, 3].map((n) => now.getFullYear() - n);
    return years.map((year) => `${year}-${month}-${day}`);
  };

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const dates = getPastDates();
        // Open-Meteo allows one date per request, so fetch in parallel
        const results = await Promise.all(
          dates.map(async (date) => {
            const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${date}&end_date=${date}&hourly=temperature_2m,weathercode&timezone=auto`;
            const res = await fetch(url);
            if (!res.ok) throw new Error("API error");
            const data = await res.json();
            return { date, data };
          })
        );
        setHistory(results);
      } catch (err) {
        setError("Could not fetch weather history.");
      } finally {
        setLoading(false);
      }
    };
    if (lat && lon) fetchHistory();
  }, [lat, lon]);

  // Fetch historical events from Wikipedia
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const day = now.getDate();
        const url = `https://en.wikipedia.org/api/rest_v1/feed/onthisday/events/${month}/${day}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("API error");
        const data = await res.json();
        setEvents(data.events || []);
      } catch (err) {
        setEventsError("Could not fetch historical events.");
      } finally {
        setEventsLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Try to get country from displayData (passed as cityName/country)
  // Accept both city and country for filtering
  const [country, setCountry] = useState("");
  useEffect(() => {
    // Try to extract country from cityName if possible (e.g., "London, GB")
    if (cityName && cityName.includes(",")) {
      setCountry(cityName.split(",").pop().trim());
    } else if (cityName && cityName.length === 2) {
      setCountry(cityName); // country code
    } else {
      setCountry("");
    }
  }, [cityName]);

  // Filter events by city or country
  const locationFilter = (event) => {
    if (!cityName && !country) return true;
    const text = event.text.toLowerCase();
    const city = cityName ? cityName.toLowerCase() : "";
    const countryLc = country ? country.toLowerCase() : "";
    return (
      (city && text.includes(city)) ||
      (countryLc && text.includes(countryLc))
    );
  };

  // Categorize events by type (sports, entertainment, etc.)
  const getEventCategory = (event) => {
    const text = event.text.toLowerCase();
    if (text.match(/olympic|world cup|championship|tournament|final|league|football|soccer|basketball|tennis|cricket|golf|nba|nfl|mlb|fifa|motogp|grand prix|race|marathon|athletics|hockey|rugby|boxing|wrestling|cycling|swimming|skating|skiing|baseball|auto racing|motorsport/)) {
      return "Sports";
    }
    if (text.match(/film|movie|cinema|actor|actress|oscar|emmy|grammy|bafta|theatre|broadway|concert|music|album|song|band|artist|festival|entertainment|tv|television|series|show|premiere|award/)) {
      return "Entertainment";
    }
    if (text.match(/science|discovery|invention|technology|space|nasa|launch|experiment|research|medicine|medical|nobel|physics|chemistry|biology|astronomy|mathematics|computer|robot|ai|artificial intelligence/)) {
      return "Science & Technology";
    }
    if (text.match(/politic|president|prime minister|government|election|treaty|war|battle|revolution|independence|constitution|law|parliament|senate|congress|minister|ambassador|diplomat|coup|monarch|king|queen|emperor|empire|republic|state|nation|country|city|capital/)) {
      return "Politics & History";
    }
    return "Other";
  };

  // Group filtered events by category
  const filteredEvents = events.filter(locationFilter);
  const categorizedEvents = filteredEvents.reduce((acc, event) => {
    const cat = getEventCategory(event);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(event);
    return acc;
  }, {});

  // Helper: map weather code to emoji
  const weatherEmoji = (code) => {
    const map = {
      0: "☀️",
      1: "🌤️",
      2: "⛅",
      3: "☁️",
      45: "🌫️",
      48: "🌫️",
      51: "🌦️",
      53: "🌦️",
      55: "🌧️",
      61: "🌦️",
      63: "🌧️",
      65: "🌧️",
      71: "🌨️",
      73: "❄️",
      75: "❄️",
      80: "🌦️",
      81: "🌧️",
      82: "⛈️",
      95: "⛈️",
      96: "⛈️",
      99: "⛈️",
    };
    return map[code] || "❔";
  };

  // Helper to summarize weather for the day
  const summarize = (data) => {
    if (!data.hourly) return "No data.";
    const temps = data.hourly.temperature_2m;
    const avgTemp = (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1);
    const code = data.hourly.weathercode[12] || data.hourly.weathercode[0];
    const codeMap = {
      0: "Clear",
      1: "Mainly clear",
      2: "Partly cloudy",
      3: "Overcast",
      45: "Fog",
      48: "Depositing rime fog",
      51: "Drizzle: Light",
      53: "Drizzle: Moderate",
      55: "Drizzle: Dense",
      61: "Rain: Slight",
      63: "Rain: Moderate",
      65: "Rain: Heavy",
      71: "Snow: Slight",
      73: "Snow: Moderate",
      75: "Snow: Heavy",
      80: "Rain showers: Slight",
      81: "Rain showers: Moderate",
      82: "Rain showers: Violent",
      95: "Thunderstorm: Slight/Moderate",
      96: "Thunderstorm with hail: Slight",
      99: "Thunderstorm with hail: Heavy",
    };
    return {
      emoji: weatherEmoji(code),
      desc: codeMap[code] || "Unknown",
      avgTemp,
    };
  };

  // Animation state for fade-in
  const [showWeather, setShowWeather] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => setShowWeather(true), 200);
    }
  }, [loading, error]);
  useEffect(() => {
    if (!eventsLoading && !eventsError) {
      setTimeout(() => setShowEvents(true), 400);
    }
  }, [eventsLoading, eventsError]);

  return (
    <>
      {loading && <div>Loading weather history...</div>}
      {error && <div className="text-red-600">{error}</div>}
      {!loading && !error && (
        <ul
          className={`space-y-1 mb-4 transition-opacity duration-700 ${showWeather ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {history.map(({ date, data }, idx) => {
            const summary = summarize(data);
            return (
              <li
                key={date}
                className="flex items-center gap-2 animate-fadein"
                style={{
                  transition: 'all 0.6s cubic-bezier(0.4,0,0.2,1)',
                  transitionDelay: `${idx * 80}ms`,
                  opacity: showWeather ? 1 : 0,
                  transform: showWeather ? 'none' : 'translateY(16px)',
                }}
              >
                <span className="font-semibold w-64 inline-block">{new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}:</span>
                <span className="text-2xl mr-1">{summary.emoji}</span>
                <span className="font-medium text-gray-700">{summary.desc},</span>
                <span
                  className="ml-2 font-bold bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] bg-clip-text text-transparent text-lg"
                >
                  avg {summary.avgTemp}°C
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <h4 className="font-semibold text-md mt-4 mb-2">Notable Events on This Day</h4>
      {eventsLoading && <div>Loading historical events...</div>}
      {eventsError && <div className="text-red-600">{eventsError}</div>}
      {!eventsLoading && !eventsError && (
        <div
          className={`transition-opacity duration-700 ${showEvents ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {filteredEvents.length > 0 ? (
            <>
              {Object.entries(categorizedEvents).map(([category, events]) => (
                <div key={category} className="mb-2">
                  <div className="font-semibold text-sm text-blue-700 mb-1">{category}</div>
                  <ul className="list-disc pl-5 space-y-1">
                    {events.slice(0, 3).map((event, idx) => (
                      <li
                        key={event.pageid || `${event.year}-${event.text.slice(0, 30).replace(/\s+/g, "-")}`}
                        className="transition-transform duration-500 hover:scale-[1.025]"
                        style={{
                          transitionDelay: `${idx * 60}ms`,
                          opacity: showEvents ? 1 : 0,
                          transform: showEvents ? 'none' : 'translateY(10px)',
                        }}
                      >
                        <span className="font-semibold">{event.year}:</span> {event.text}
                        {event.pages && event.pages[0] && (
                          <a
                            href={`https://en.wikipedia.org/?curid=${event.pages[0].pageid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline ml-1 hover:text-blue-400 transition-colors duration-200"
                          >
                            [Read more]
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          ) : (
            <div className="text-gray-500">No city/country-related events found for this day.</div>
          )}
        </div>
      )}
    </>
  );
};

export default WeatherHistory;
